import { CommerceAPI } from "commerce-api"
import { StoreModel } from "./utils"
import { Status, StatusMap } from "../types/commerce"
import { RootStore } from "./RootStore"
import { makeObservable, observable, action, flow, computed } from "mobx"
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { BookRapidDeliveryResponse, RapidDeliveryStoreInitialState, RapidDeliveryReservation } from '../types/store/rapidDelivery'
import { RapidBasketCapacityState } from '../types/store/basket'

export class RapidDeliveryStore implements StoreModel {
  api: CommerceAPI
  status: Status
  rootStore: RootStore
  isRapidDeliveryAvailableForPostcode: boolean
  rapidDeliveryShippingCost: number | null
  rapidDeliveryMaxItems: number | null
  rapidDeliveryMaxWeight: number | null
  rapidBasketCapacityState: RapidBasketCapacityState
  lastShownRapidBasketCapacityState: RapidBasketCapacityState
  rapidBasketCapacityModalOpen: boolean
  rapidBasketAnimationTick: number
  constructor(rootStore: RootStore, initialState?: RapidDeliveryStoreInitialState) {
    this.api = rootStore.api
    this.status = initialState?.status ?? StatusMap.IDLE
    this.isRapidDeliveryAvailableForPostcode = initialState?.isRapidDeliveryAvailableForPostcode || false
    this.rapidDeliveryShippingCost = initialState?.rapidDeliveryShippingCost || null
    this.rapidDeliveryMaxItems = initialState?.rapidDeliveryMaxItems || null
    this.rapidDeliveryMaxWeight = initialState?.rapidDeliveryMaxWeight || null
    this.rapidBasketCapacityState = initialState?.rapidBasketCapacityState || 'none'
    this.lastShownRapidBasketCapacityState = initialState?.lastShownRapidBasketCapacityState || 'none'
    this.rapidBasketCapacityModalOpen = initialState?.rapidBasketCapacityModalOpen || false
    this.rapidBasketAnimationTick = initialState?.rapidBasketAnimationTick || 0
    this.rootStore = rootStore

    makeObservable(this, {
      status: observable,
      isRapidDeliveryAvailableForPostcode: observable,
      rapidBasketCapacityState: observable,
      lastShownRapidBasketCapacityState: observable,
      rapidBasketCapacityModalOpen: observable,
      rapidBasketAnimationTick: observable,
      setRapidDeliveryAvailability: action.bound,
      setRapidBasketCapacityState: action.bound,
      setLastShownRapidBasketCapacityState: action.bound,
      setRapidBasketCapacityModalOpen: action.bound,
      triggerRapidBasketAnimation: action.bound,
      updateRapidBasketCapacityAlert: action.bound,
      resetRapidBasketCapacityAlert: action.bound,
      bookRapidDelivery: flow.bound,
      rapidDeliveryShippingCost: observable,
      rapidDeliveryMaxItems: observable,
      rapidDeliveryMaxWeight: observable,
      isBasketOverRapidDeliveryItemLimit: computed,
      getRapidDeliveryETA: flow.bound
    })
  }

  setRapidDeliveryAvailability(value: boolean) {
    this.isRapidDeliveryAvailableForPostcode = value
  }

  setRapidDeliveryMaxItems(value: number | null) {
    this.rapidDeliveryMaxItems = value
  }
  
  setRapidDeliveryMaxWeight(value: number | null) {
    this.rapidDeliveryMaxWeight = value
  }

  setRapidDeliveryShippingCost(value: number | null) {
    this.rapidDeliveryShippingCost = value
  }

  setRapidBasketCapacityState(state: RapidBasketCapacityState) {
    this.rapidBasketCapacityState = state
  }

  setLastShownRapidBasketCapacityState(state: RapidBasketCapacityState) {
    this.lastShownRapidBasketCapacityState = state
  }

  setRapidBasketCapacityModalOpen(open: boolean) {
    this.rapidBasketCapacityModalOpen = open
  }

  triggerRapidBasketAnimation() {
    this.rapidBasketAnimationTick += 1
  }

  resetRapidBasketCapacityAlert() {
    this.setRapidBasketCapacityState('none')
    this.setLastShownRapidBasketCapacityState('none')
    this.setRapidBasketCapacityModalOpen(false)
  }

  resolveRapidBasketCapacityState(): RapidBasketCapacityState {
    if (!this.rootStore.basketStore.basket?.c_isRapidDeliverySelected) {
      return 'none'
    }

    const { progress } = this.rootStore.basketStore.basketLimitThreshold ?? {}
    if (typeof progress !== 'number') return 'none'
    if (progress >= 100) return 'fullLimit'
    if (progress > 90) return 'nearLimit'
    return 'none'
  }

  updateRapidBasketCapacityAlert(previousBasketCount: number) {
    const basket = this.rootStore.basketStore.basket
    const nextCapacityState = this.resolveRapidBasketCapacityState()
    this.setRapidBasketCapacityState(nextCapacityState)

    if (nextCapacityState === 'none') {
      this.setRapidBasketCapacityModalOpen(false)
      this.setLastShownRapidBasketCapacityState('none')
      return
    }

    // Always re-show the modal on every ATB attempt when already at full capacity
    if (nextCapacityState === 'fullLimit') {
      this.setRapidBasketCapacityModalOpen(true)
      this.setLastShownRapidBasketCapacityState(nextCapacityState)
      this.triggerRapidBasketAnimation()
      return
    }

    const currentBasketCount = basket?.productQuantityTotal ?? 0
    if (currentBasketCount <= previousBasketCount) {
      return
    }

    if (this.lastShownRapidBasketCapacityState !== nextCapacityState) {
      this.setRapidBasketCapacityModalOpen(true)
      this.setLastShownRapidBasketCapacityState(nextCapacityState)
      this.triggerRapidBasketAnimation()
    }
  }

  *bookRapidDelivery() {
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Rapid-BookDelivery`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )
    const result: BookRapidDeliveryResponse = yield response.json()

    if (result.basket) {
      this.rootStore.basketStore.getOrCreateBasket(result.basket)
      this.rootStore.globalStore.setSelectedRegion()
    }

    return result;
  }

  get isBasketOverRapidDeliveryItemLimit() {
    if (this.rapidDeliveryMaxItems === null) return false
    return (this.rootStore.basketStore.basket?.productQuantityTotal ?? 0) > this.rapidDeliveryMaxItems
  }

  get isRapidDeliveryAvailable() {
    return this.rootStore.globalStore.customSitePreferences?.enableRapidDelivery as boolean
  }

  get rapidDeliverySoftReserveTimeLimit() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliverySoftReserveTime as number
  }

  get rapidDeliveryFallback() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryFallback as string
  }

  get rapidDeliveryServiceUnavailableMessage() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryServiceUnavailable as boolean
  }

  get rapidDeliveryPricebookEnabled() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryPricebookEnabled as boolean
  }

  get isRapidDeliveryBooked() {
    return this.rootStore.basketStore.basket?.c_isRapidDeliverySelected ?? false
  }

  get rapidDeliveryDeliveryEstimate() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryDeliveryEstimate as number ?? 60
  }

  get isRapidSponsoredProductsHidden() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryHideSponsoredProducts as boolean
  }

  get rapidDeliveryReservation(): RapidDeliveryReservation | null {
    const basket = this.rootStore.basketStore.basket
    if (!basket?.c_slotExpiryTime || !this.isRapidDeliveryBooked) {
      return null
    }
    return {
      expiryTime: basket.c_slotExpiryTime,
      windowStartTime: basket.c_windowStartTime || null,
      windowEndTime: basket.c_windowEndTime || null,
      shippingCost: basket.shippingItems?.[0].c_deliveryCharge ?? null
    }
  }

  *getRapidDeliveryETA(postcode: string){
  const res: Response = yield fetch(`${getAppOrigin()}/api/getDeliveryETA`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: this.api.auth.authToken,
      },
      body: JSON.stringify({ postcode: postcode })
    })

    const json = (yield res.json()) as Record<string, unknown>

    return json
  }

  get asJson() {
    return {
      status: this.status,
      isRapidDeliveryBooked: this.isRapidDeliveryBooked,
      isRapidDeliveryAvailableForPostcode: this.isRapidDeliveryAvailableForPostcode,
      rapidDeliveryReservation: this.rapidDeliveryReservation,
      rapidBasketCapacityState: this.rapidBasketCapacityState,
      lastShownRapidBasketCapacityState: this.lastShownRapidBasketCapacityState,
      rapidBasketCapacityModalOpen: this.rapidBasketCapacityModalOpen,
      rapidBasketAnimationTick: this.rapidBasketAnimationTick,
    }
  }
}